import React from 'react';
import {
    RecallItems,
    RecallsApiResponse,
} from '../../../services/recalls-service/recalls-service';
import { StringUtil } from '../../../components/utils/string-util/string-util';
import AccordionItem from '../../components/AccordionItem/AccordionItem';
import { RecallsProps } from '../recalls/hook/use-recalls-content';
import warningSVG from '../../../assets/recalls-warning.svg';
import { ExemptWordsListProps } from '../recalls/hook/use-exempt-word-list';
import {
    COUNTRY_CODES_CARIBBEAN,
    COUNTRY_CODES_CENTRAL_AMERICA,
} from '../../support-constants';
import ServiceHandler from '../../../../src/services/service-handler';
import { NhtsaAccordionContent } from './nhtsa-accordion-content';
import { RecallsDataAccordionDescriptionLang } from './recalls-data-accordion-description-lang';
import { RecallsDataAccordionContent } from './recalls-data-accordion-content';
import { RecallsAccordionNextStepsDetails } from './recalls-accordion-next-steps-details';
import { isIMGTakataRoute, isIMGTakataCampaignIdPresent } from './recalls-util';

const {
    current3LetterCountryCode,
    currentLanguageRegionCode,
} = ServiceHandler.AppConfigurationService;

interface RecallsAccordionParams {
    exemptWordsContent: ExemptWordsListProps | null;
    recallData: RecallsApiResponse | undefined | null;
    recallsContent: RecallsProps;
    isWebview: boolean;
    isYmm: boolean;
    isLincoln: boolean;
    model?: string;
    isImgTakataCampaignIdPresent: boolean;
    isImgTakataRoute: boolean;
}

const RecallsAccordion = (params: RecallsAccordionParams) => {
    const {
        exemptWordsContent,
        recallData,
        recallsContent,
        isWebview,
        isYmm,
        isLincoln,
        model,
        isImgTakataCampaignIdPresent,
        isImgTakataRoute,
    } = params;
    const showOrHideRecallData = isImgTakataRoute
        ? recallData?.recallsCount &&
          recallData?.recallsCount > 0 &&
          isImgTakataCampaignIdPresent
        : recallData?.recallsCount && recallData?.recallsCount > 0;

    const CENTRAL_AMERICA_AND_CARIBBEAN_EN_ES_MARKETS = [
        ...COUNTRY_CODES_CENTRAL_AMERICA.CENTRAL_AMERICA_MARKETS_SPANISH,
        ...COUNTRY_CODES_CARIBBEAN.CARIBBEAN_MARKETS_ENGLISH,
        ...COUNTRY_CODES_CARIBBEAN.CARIBBEAN_MARKETS_SPANISH,
    ];
    function getDescriptionLang(descriptionLang: string) {
        if (!descriptionLang) {
            return ' ';
        }
        if (exemptWordsContent) {
            return StringUtil.sentenceCase(descriptionLang, exemptWordsContent);
        } else {
            return descriptionLang;
        }
    }
    function getSubtitle(recall: RecallItems) {
        if (model) {
            return '';
        }
        if (
            typeof recall.remedyAvailable !== 'undefined' &&
            !recall.remedyAvailable &&
            recallsContent?.remedyNotAvailableLabel
        ) {
            return `${recallsContent?.detailsRecallIncomplete}, ${recallsContent.remedyNotAvailableLabel}`;
        } else {
            return `${recallsContent?.detailsRecallIncomplete}`;
        }
    }
    if (showOrHideRecallData) {
        return (
            <div className="recalls-section" id="recalls-section">
                <div className="section-heading-link" id="recalls" />

                <div className="recalls-section-heading">
                    <img
                        src={warningSVG}
                        alt=""
                        className="recall-warning-icon"
                        loading="lazy"
                    />
                    <h2>{recallsContent.detailsRecallsAccordionLabel}</h2>
                </div>
                <div className="support-accordion">
                    {recallData?.nhtsa?.map(recall => {
                        return (
                            <AccordionItem
                                title={getDescriptionLang(
                                    recall.descriptionLang
                                )}
                                id={recall.nhtsaRecallNumber}
                                subtitle={getSubtitle(recall)}
                                key={recall.descriptionLang}
                            >
                                <div className="support-accordion-description-label">
                                    {recallsContent?.detailsRecallNumberLabel}
                                </div>
                                <div className="support-accordion-description-content">
                                    {recall.manufacturerRecallNumber}/
                                    {recall.nhtsaRecallNumber}
                                </div>
                                <div className="support-accordion-description-label">
                                    {recallsContent?.detailsIssueDateLabel}
                                </div>
                                <div className="support-accordion-description-content">
                                    {recall.recallDate}
                                </div>
                                <div className="support-accordion-description-label">
                                    {
                                        recallsContent?.detailsAccordionDescriptionLabel
                                    }
                                </div>

                                <NhtsaAccordionContent
                                    recallsContent={recallsContent}
                                    exemptWordsContent={exemptWordsContent}
                                    recall={recall}
                                />
                                {recallsContent?.detailsNextStepsLabel &&
                                    !isYmm &&
                                    !isLincoln &&
                                    !isWebview && (
                                        <RecallsAccordionNextStepsDetails
                                            recallsContent={recallsContent}
                                            exemptWordsContent={
                                                exemptWordsContent
                                            }
                                        />
                                    )}
                            </AccordionItem>
                        );
                    })}
                    {recallData?.recalls?.map(recall => {
                        if (
                            isIMGTakataRoute() &&
                            !isIMGTakataCampaignIdPresent(
                                currentLanguageRegionCode,
                                recall.fsaNumber
                            )
                        ) {
                            return null;
                        }
                        return (
                            <AccordionItem
                                title={getDescriptionLang(
                                    recall.descriptionLang
                                )}
                                id={recall.fsaNumber}
                                subtitle={getSubtitle(recall)}
                                key={recall.descriptionLang}
                            >
                                <div className="support-accordion-description-label">
                                    {
                                        recallsContent?.detailsAccordionDescriptionLabel
                                    }
                                </div>
                                {!CENTRAL_AMERICA_AND_CARIBBEAN_EN_ES_MARKETS.includes(
                                    current3LetterCountryCode
                                ) ? (
                                    <div className="support-accordion-description-content">
                                        {getDescriptionLang(
                                            recall.descriptionLang
                                        ) || recall.descriptionLang
                                            ? StringUtil.sentenceCaseWithoutSanitize(
                                                  recall.descriptionLang
                                              )
                                            : ' '}
                                    </div>
                                ) : (
                                    <>
                                        <RecallsDataAccordionDescriptionLang
                                            recall={recall}
                                            exemptWordsContent={
                                                exemptWordsContent
                                            }
                                        />{' '}
                                        <RecallsDataAccordionContent
                                            recall={recall}
                                            recallsContent={recallsContent}
                                            exemptWordsContent={
                                                exemptWordsContent
                                            }
                                        />
                                    </>
                                )}
                                <div className="support-accordion-description-label">
                                    {recallsContent?.detailsCampaignNumberLabel}
                                </div>
                                <div className="support-accordion-description-content">
                                    {recall.fsaNumber}
                                </div>
                            </AccordionItem>
                        );
                    })}
                </div>
            </div>
        );
    }
    return recallData && recallsContent.detailsNoRecallsLabel ? (
        <div className="recalls-section no-recalls">
            <div className="section-heading-link" id="recalls" />
            <div className="recalls-section-heading">
                <img
                    src={
                        process.env.REACT_APP_AEM_BASE_URL +
                        recallsContent.greenCheckMark
                    }
                    alt=""
                    loading="lazy"
                />
                <h2>{recallsContent.detailsNoRecallsLabel}</h2>
            </div>
            {recallsContent.detailsNoRecallsDescription && (
                <div className="recalls-no-action-needed-description">
                    {recallsContent.detailsNoRecallsDescription}
                </div>
            )}
        </div>
    ) : null;
};

export default RecallsAccordion;
